import { jsx } from 'react/jsx-runtime';
import { defaultBaseline, defaultFonts, safeRest } from '@wds/utils';
import { createMemoStyles, SEMANTIC_COLORS } from '@wds/styles';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

var useStyles = createMemoStyles(function (theme) {
  var _a = theme.type,
    fontSize = _a.fontSize,
    fontWeight = _a.fontWeight,
    fontFamily = _a.fontFamily,
    palette = theme.palette,
    spacing = theme.spacing,
    border = theme.border,
    radius = theme.radius;
  var avatar = {
    height: {
      sm: spacing[5],
      md: spacing[6],
      lg: spacing[8],
      xl: spacing[9]
    },
    borderWidth: {
      sm: spacing[1],
      md: spacing[1],
      lg: spacing[2],
      xl: spacing[2]
    },
    borderRadius: {
      square: radius.small,
      rounded: radius.full
    },
    border: border.light
  };
  var borderHoverColor = {
    default: palette.background.hover,
    onDark: palette.background.hoverOnDark
  };
  var borderActiveColor = {
    default: palette.background.pressed,
    onDark: palette.background.pressedOnDark
  };
  return {
    wrenchAvatar: __assign(__assign(__assign({}, defaultBaseline), defaultFonts(theme)), {
      fontWeight: fontWeight.medium,
      fontFamily: fontFamily.base,
      display: 'inline-block',
      borderRadius: function (_a) {
        var shape = _a.shape;
        return avatar.borderRadius[shape !== null && shape !== void 0 ? shape : 'rounded'];
      },
      border: 'solid 4px transparent',
      borderWidth: function (_a) {
        var size = _a.size;
        return avatar.borderWidth[size !== null && size !== void 0 ? size : 'md'];
      },
      backgroundColor: 'transparent',
      height: function (_a) {
        var size = _a.size;
        return avatar.height[size !== null && size !== void 0 ? size : 'md'];
      },
      width: function (_a) {
        var size = _a.size;
        return avatar.height[size !== null && size !== void 0 ? size : 'md'];
      },
      minWidth: function (_a) {
        var size = _a.size;
        return avatar.height[size !== null && size !== void 0 ? size : 'md'];
      },
      textAlign: 'center',
      overflowX: 'hidden',
      boxSizing: 'content-box',
      '&:hover': {
        borderColor: function (_a) {
          var onDark = _a.onDark,
            interactive = _a.interactive;
          return !interactive ? 'transparent' : onDark ? borderHoverColor.onDark : borderHoverColor.default;
        },
        cursor: function (_a) {
          var interactive = _a.interactive;
          return interactive ? 'pointer' : null;
        }
      },
      '&:active': {
        borderColor: function (_a) {
          var onDark = _a.onDark,
            interactive = _a.interactive;
          return !interactive ? 'transparent' : onDark ? borderActiveColor.onDark : borderActiveColor.default;
        }
      }
    }),
    wrenchAvatarBody: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: '100%',
      height: '100%',
      color: palette.text.dark,
      backgroundColor: function (_a) {
        var color = _a.color,
          onDark = _a.onDark;
        return color === 'secondary' ? palette.muted.main : onDark ? palette.background[color !== null && color !== void 0 ? color : 'primary'].medium : palette.background[color !== null && color !== void 0 ? color : 'primary'].medium;
      },
      fontSize: function (_a) {
        var size = _a.size;
        return {
          sm: fontSize.caption,
          md: fontSize.bodySm,
          lg: fontSize.pageTitle,
          xl: fontSize.hero1
        }[size !== null && size !== void 0 ? size : 'md'];
      },
      '& img': {
        display: 'block',
        width: '100%',
        minHeight: '100%'
      }
    }
  };
});

var Avatar = function Avatar(_a) {
  var children = _a.children,
    _b = _a.color,
    color = _b === void 0 ? 'primary' : _b,
    _c = _a.shape,
    shape = _c === void 0 ? 'rounded' : _c,
    _d = _a.size,
    size = _d === void 0 ? 'md' : _d,
    _e = _a.onDark,
    onDark = _e === void 0 ? false : _e,
    _f = _a.disabled,
    disabled = _f === void 0 ? false : _f,
    onClick = _a.onClick,
    rest = __rest(_a, ["children", "color", "shape", "size", "onDark", "disabled", "onClick"]);
  var interactive = !!onClick && !disabled;
  var _g = useStyles({
      color: color,
      shape: shape,
      size: size,
      interactive: interactive,
      onDark: onDark
    }),
    wrenchAvatar = _g.wrenchAvatar,
    wrenchAvatarBody = _g.wrenchAvatarBody;
  var AvatarBody = function AvatarBody() {
    return jsx("span", {
      className: wrenchAvatarBody,
      children: children
    });
  };
  if (interactive) {
    return jsx("button", __assign({
      onClick: onClick,
      className: wrenchAvatar
    }, safeRest(rest), {
      children: jsx(AvatarBody, {})
    }));
  }
  return jsx("span", __assign({
    className: wrenchAvatar
  }, safeRest(rest), {
    children: jsx(AvatarBody, {})
  }));
};

var AVATAR_SHAPES = ['rounded', 'square'];
var AVATAR_SIZES = ['sm', 'md', 'lg', 'xl'];
var AVATAR_COLORS = SEMANTIC_COLORS;

export { AVATAR_COLORS, AVATAR_SHAPES, AVATAR_SIZES, Avatar };
